import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {readFile, ReadingFileAs} from "../../../../../../../../core/services/utils/utils";
import Utils from "../../../../../../../../core/services/utils/utils";
import {InputFileAcceptTypes} from "../../../../../../../../core/constants/enums";
import Api from "../../../../../../../../core/services/api/api";
import {OutlinedDropdown} from "../../../../../../../components/base/dropdown/outlined_dropdown";
import NewDropZone from "../../../../../../../components/base/new-drop-zone";
import classnames from "classnames";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {Delete, Refresh} from "@mui/icons-material";
import {CircularProgress, Tooltip} from "@mui/material";
import {OutlinedInput} from "../../../../../../../components/base/input/outlined_input";
import Button from "../../../../../../../components/base/button/button";
import {ReactComponent as Placeholder} from "../../../../../../../../assets/images/dropzone/upload-placeholder.svg";

const initialPrintConfig = {
    printer: null,
    pdfFile: null,
    name: null,
    queueId: null,
    copyCount: '1',
}

const PrintHubViewHubInfo = forwardRef(({selectedHub, onPDFPrinted, setPrinterList}, ref) => {
    const [printConfig, setPrintConfig] = useState(initialPrintConfig);
    const [sendingForPrint, setSendingForPrint] = useState(false);
    const [refreshingPrinterList, setRefreshingPrinterList] = useState(false);
    const isMounted = useIsMounted();

    /**@type {boolean}*/
    const fileSelected = useMemo(() => (!!printConfig.pdfFile || !!printConfig.queueId), [printConfig.pdfFile, printConfig.queueId]);

    /**
     * Sets the print Config from the queue.
     * @type {(function(*): void)|*}
     */
    const setConfigFromQueue = useCallback((queue) => {
        setPrintConfig(prevState => ({
            ...prevState,
            name: queue.name,
            printer: selectedHub?.printers?.find(e => e.name === queue.printerName),
            fileName: queue.fileName,
            queueId: queue.id,
        }));
    }, [selectedHub?.printers])

    /**
     * Exposes the setConfigFromQueue function to the parent component.
     */
    useImperativeHandle(ref, () => ({
        setConfigFromQueue: setConfigFromQueue,
    }), [setConfigFromQueue]);

    /**
     * Resets the print config and print histories when the selected hub changes.
     */
    useEffect(() => {
        setPrintConfig(initialPrintConfig);
    }, [selectedHub?.id])

    /**
     * Prints the PDF file with the given config and adds it to the print histories list.
     * @type {(function(): void)}
     */
    const printPDF = useCallback(async () => {
        setSendingForPrint(true);
        let response;
        if (!printConfig.queueId) {
            const buffer = await readFile(printConfig.pdfFile, ReadingFileAs.binaryString);
            const forApi = {
                fileContent: btoa(buffer),
                fileName: Utils.sanitizeFileName(printConfig.pdfFile.name),
                name: printConfig.name,
                copyCount: parseInt(printConfig.copyCount),
                partnerHubId: selectedHub.id,
                printerId: printConfig.printer.id
            }
            response = await (Api.createPrinterHubQueue(forApi));
        } else {
            const forApi = {
                originalQueueId: printConfig.queueId,
                name: printConfig.name,
                partnerHubId: selectedHub.id,
                printerId: printConfig.printer.id,
                copyCount: parseInt(printConfig.copyCount),
            }
            response = await (Api.copyPrinterHubQueue(forApi));
        }
        if (!isMounted())
            return;
        setSendingForPrint(false);
        if (response?.resultFlag) {
            onPDFPrinted(response.data);
            setPrintConfig(initialPrintConfig);
        }
    }, [isMounted, onPDFPrinted, printConfig, selectedHub?.id]);

    /**
     * Refreshes the printer list.
     * @type {(function(): Promise<void>)|*}
     */
    const refreshPrinterList = useCallback(async () => {
        setRefreshingPrinterList(true);
        const response = await Api.getPrinterHubs();
        if (!isMounted())
            return;
        setRefreshingPrinterList(false);
        if (response?.resultFlag) {
            const newList = response.data?.find(e => e.id === selectedHub?.id)?.printers ?? [];
            setPrinterList(newList);
            if (!printConfig.queueId && !newList.map(e => e.id).includes(printConfig.printer?.id)) {
                setPrintConfig(prevState => ({
                    ...prevState,
                    printer: null,
                }));
            }
        }
    }, [isMounted, printConfig.printer?.id, printConfig.queueId, selectedHub?.id, setPrinterList])

    /**
     * Sets the print config from the given file list.
     * @type {function(File[]): void}
     */
    const onPDFFileDropped = useCallback((fileList) => {
        setPrintConfig(prevState => ({
            ...prevState,
            pdfFile: fileList[0],
            name: fileList[0]?.name,
            queueId: null,
        }))
    }, [])

    /**
     * Removes the PDF file from the print config.
     * @type {(function(*): void)}
     */
    const removePDF = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        setPrintConfig(prevState => ({
            ...prevState,
            pdfFile: null,
            queueId: null,
            name: null,
        }));
    }, []);

    /**
     * Changes the name of the print config.
     * @type {function(string): void}
     */
    const changeName = useCallback((name) =>
        setPrintConfig(prevState => ({
            ...prevState,
            name: name ?? ''
        })), []);

    /**
     * Changes the copy count of the print config.
     * @type {(function(*, boolean=): void)}
     */
    const changeCopyCount = useCallback((countStr, commit = false) => {
        countStr = countStr?.trim() ?? '';
        if (commit) {
            let num = parseInt(countStr);
            if (Number.isNaN(num) || num <= 0) {
                countStr = initialPrintConfig.copyCount;
            }
        }
        setPrintConfig(prevState => ({
            ...prevState,
            copyCount: countStr,
        }));
    }, [])

    return (
        <>
            <div className={'w-100 d-flex flex-column'}>
                <p className={'mini-title'}>
                    Print a new PDF
                </p>
                <div className={'d-flex w-100 align-items-center'}>
                    <OutlinedDropdown
                        label={'Printer'}
                        className={'my-3'}
                        disabled={sendingForPrint}
                        onChange={(printer) => setPrintConfig(prevState => ({...prevState, printer: printer}))}
                        value={printConfig.printer ?? ''}
                        data={selectedHub?.printers.map(e => ({
                            id: e.id, label: e.name, value: e
                        }))}
                    />
                    <IconButton
                        disabled={sendingForPrint || refreshingPrinterList}
                        className={"mx-1 ml-2"}
                        color={'paper.secondary'}
                        onPressed={() => refreshPrinterList()}>
                        {
                            refreshingPrinterList
                                ? <CircularProgress size={15} color={'primary'}/>
                                : <Refresh className={"icon normal"}/>
                        }
                    </IconButton>
                </div>
                {
                    !!printConfig.printer &&
                    <NewDropZone
                        noParse
                        setFiles={onPDFFileDropped}
                        uploading={sendingForPrint}
                        loading={sendingForPrint}
                        disabled={sendingForPrint}
                        openOnClick={true}
                        accept={[InputFileAcceptTypes.pdf]}
                        multiple={false}
                        id={'print-hub'}
                        notAcceptedDurationInMs={2000}
                    >
                        {(_, highlight, notAccepted) => (
                            <>
                                <div className={classnames('pdf-dropzone')}>
                                    {
                                        fileSelected &&
                                        <div className={'d-flex align-items-center justify-content-between'}>
                                            <p className={'mb-0 text-md font-weight-semi-bold'}>
                                                {
                                                    printConfig.pdfFile?.name ?? printConfig.name ?? ''
                                                }
                                            </p>
                                            <IconButton
                                                disabled={sendingForPrint}
                                                className={"mx-1 only-on-hover"}
                                                color={'paper.error'}
                                                size={'small'}
                                                component={'span'}
                                                onClick={removePDF}
                                            >
                                                <Delete className={'icon small'}/>
                                            </IconButton>
                                        </div>
                                    }
                                    {
                                        !fileSelected &&
                                        <Placeholder/>
                                    }
                                    <div className={classnames('call-to-action', {'not-accepted': notAccepted})}
                                    >
                                        {
                                            notAccepted
                                                ? "This format is not supported. Use a PDF Document"
                                                : "Drag & Drop a New Picture"
                                        }
                                    </div>
                                </div>
                            </>
                        )}
                    </NewDropZone>
                }
                {
                    fileSelected &&
                    <>
                        <Tooltip title={'Used for future reference in Queue Section'}>
                            <span>
                                <OutlinedInput
                                    className={'mt-3'}
                                    fullWidth
                                    disabled={sendingForPrint}
                                    label={"Name"}
                                    value={printConfig.name ?? ''}
                                    onChange={changeName}
                                    type={'text'}
                                />
                            </span>
                        </Tooltip>
                        <OutlinedInput
                            className={'mt-3'}
                            fullWidth
                            disabled={sendingForPrint}
                            label={"Copy Count"}
                            value={printConfig.copyCount ?? ''}
                            onChange={(val) => changeCopyCount(val, false)}
                            onBlur={(e) => changeCopyCount(e.target.value, true)}
                            type={'number'}
                        />
                        <Button
                            disabled={sendingForPrint}
                            variant={'contained'}
                            color={'primary'}
                            className={'mt-3'}
                            onClick={printPDF}
                        >
                            {
                                !sendingForPrint
                                    ? "Print"
                                    : "Printing..."
                            }
                        </Button>
                    </>
                }
            </div>
        </>
    );
})

export default PrintHubViewHubInfo;
